import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { PortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type { SendRecipient } from "@/dashboard/inventory_management/send/sendRecipient";
import { cnmcApi } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcApi";
import { cnmcOutboundApi } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcOutboundApi";
import {
  isCnmcRecipientComplete,
  normalizeCnmcCode,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcRecipientRules";
import type {
  CnmcContext,
  CnmcFilingProfile,
  CnmcRecipientDraft,
  OutboundFiling,
  OutboundPreviewRequest,
  OutboundPreviewResponse,
  OutboundRecipientInput,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import { computed, reactive, ref, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  cnmcSendTarget,
  outboundRepreviewNoticeKey,
  type SendKind,
} from "./cnmcOutboundRules";

/**
 * @property companyId - The company whose CNMC holdings are sent
 * @property recipient - Send's recipient: a CNMC recipient files its name, and an export its EECS account
 */
export interface UseCnmcOutboundSendOptions {
  selection: PortfolioSelection<SendKind>;
  companyId: () => number;
  recipient: Ref<SendRecipient>;
}

export type CnmcOutboundSend = ReturnType<typeof useCnmcOutboundSend>;

/**
 * The state and API calls of a CNMC transfer or export from synced holdings: the recipient of the
 * selection's connection and route, its preview and review, the filing it creates (re-previewing on a
 * 409), the suggested oldest-first selection and the connection's filing details.
 */
export function useCnmcOutboundSend(options: UseCnmcOutboundSendOptions) {
  const { selection, companyId, recipient: sendRecipient } = options;
  const { t } = useI18n();
  const { globalErrorHandler } = useGlobalHandlers();

  // Null for registry-API sends and no selection.
  const target = computed(() => cnmcSendTarget(selection.selectionKey));
  const connectionId = computed(() => target.value?.connectionId ?? null);
  const route = computed(() => target.value?.route ?? null);

  const context = ref<CnmcContext | null>(null);
  const destinations = computed(() => context.value?.destinations ?? []);
  const connection = computed(() =>
    context.value?.connections.find(
      (candidate) => candidate.id === connectionId.value
    )
  );

  const r2 = ref("");
  const preview = ref<OutboundPreviewResponse | null>(null);
  const notice = ref<string | null>(null);
  const isReviewOpen = ref(false);
  const isPreviewing = ref(false);
  const isConfirming = ref(false);
  const isBusy = computed(() => isPreviewing.value || isConfirming.value);

  const loadContext = async () => {
    try {
      context.value = (await cnmcApi.context(companyId())).data;
    } catch (error) {
      globalErrorHandler(error);
    }
  };

  // Immediate, so a selection restored from the URL against already-cached rows loads it too.
  watch(
    connectionId,
    (current) => {
      if (current !== null && !context.value) void loadContext();
    },
    { immediate: true }
  );

  // The name and EECS account are Send's, so they carry over between modes and an uploaded document
  // fills them once; only the R2 code is a CNMC field of its own. Editing the name or the account
  // names an external recipient, never a hosted pick.
  const recipient = computed<CnmcRecipientDraft | null>({
    get: () =>
      route.value
        ? {
            route: route.value,
            name: sendRecipient.value.companyName,
            r2: r2.value,
            eecs: sendRecipient.value.eecsAccountNumber,
          }
        : null,
    set: (draft) => {
      if (!draft) return;
      r2.value = draft.r2;
      const current = sendRecipient.value;
      if (
        draft.name !== current.companyName ||
        draft.eecs !== current.eecsAccountNumber
      )
        sendRecipient.value = {
          ...current,
          companyId: null,
          companyName: draft.name,
          eecsAccountNumber: draft.eecs,
        };
    },
  });

  const isComplete = computed(
    () =>
      !!recipient.value &&
      isCnmcRecipientComplete(recipient.value, destinations.value)
  );

  const request = computed(
    (): OutboundPreviewRequest & { recipient: OutboundRecipientInput } => {
      const name = sendRecipient.value.companyName.trim();
      return {
        portfolio_items: selection.selectedItemsForApi.map(
          ({ id, selected_count }) => ({ id, selected_count })
        ),
        recipient:
          route.value === "DOMESTIC"
            ? { name, r2: normalizeCnmcCode(r2.value) }
            : {
                name,
                eecs: normalizeCnmcCode(sendRecipient.value.eecsAccountNumber),
              },
      };
    }
  );

  /** Previews the current selection, keeping any notice; false when the preview could not be loaded. */
  const loadPreview = async (): Promise<boolean> => {
    const current = connectionId.value;
    if (current === null) return false;
    isPreviewing.value = true;
    try {
      preview.value = (
        await cnmcOutboundApi.preview(companyId(), current, request.value)
      ).data;
      return true;
    } catch (error) {
      globalErrorHandler(error);
      return false;
    } finally {
      isPreviewing.value = false;
    }
  };

  /** Previews the selection afresh, without an earlier refusal's notice, and opens its review. */
  const openReview = async () => {
    notice.value = null;
    if (await loadPreview()) isReviewOpen.value = true;
  };

  const closeReview = () => {
    isReviewOpen.value = false;
  };

  /**
   * Creates the reviewed filing. Null when nothing was created: a refusal a fresh preview explains
   * re-previews with a notice (and drops the preview if that fails too, so it cannot be confirmed
   * again), any other failure reaches the error handler.
   */
  const confirm = async (): Promise<OutboundFiling | null> => {
    const current = connectionId.value;
    const reviewed = preview.value;
    if (current === null || !reviewed || isConfirming.value) return null;
    isConfirming.value = true;
    try {
      const { data } = await cnmcOutboundApi.create(companyId(), current, {
        ...request.value,
        signature: reviewed.signature,
      });
      return data;
    } catch (error) {
      const noticeKey = outboundRepreviewNoticeKey(error);
      if (noticeKey) {
        notice.value = t(noticeKey);
        if (!(await loadPreview())) preview.value = null;
      } else {
        globalErrorHandler(error);
      }
      return null;
    } finally {
      isConfirming.value = false;
    }
  };

  // The suggestion keeps every group's total and takes it oldest month first.
  const applySuggestion = async () => {
    const suggested = preview.value?.suggested_items;
    if (!suggested?.length) return;
    selection.replaceSelection(
      new Map(suggested.map((item) => [item.id, item.selected_count]))
    );
    notice.value = null;
    await loadPreview();
  };

  /**
   * Saves the connection's filing details, reloads them and re-opens the review on a fresh preview;
   * false when saving failed.
   */
  const saveProfile = async (profile: CnmcFilingProfile): Promise<boolean> => {
    const current = connectionId.value;
    if (current === null) return false;
    try {
      await cnmcApi.saveProfile(companyId(), current, profile);
    } catch (error) {
      globalErrorHandler(error);
      return false;
    }
    await loadContext();
    if (await loadPreview()) isReviewOpen.value = true;
    return true;
  };

  /** Forgets what the last send filed, once Send has cleared its recipient. */
  const reset = () => {
    r2.value = "";
    preview.value = null;
    notice.value = null;
  };

  return reactive({
    connectionId,
    route,
    recipient,
    destinations,
    connection,
    isComplete,
    isBusy,
    isReviewOpen,
    preview,
    notice,
    openReview,
    closeReview,
    confirm,
    applySuggestion,
    saveProfile,
    reset,
  });
}
